/* eslint-disable max-lines */
import { PlusOutlined } from '@ant-design/icons';
import { Form, Select, Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { DateRangePicker } from 'src/components/DateRangePicker/DateRangePicker';
import { SearchPanel } from 'src/components/SearchPanel/SearchPanel';
import {
    SearchPanelFilterForm,
    SearchPanelFilterFormProps,
} from 'src/components/SearchPanel/SearchPanelFilterForm';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { Router } from 'src/core/router/Router';
import { getApplicationStatuses } from 'src/pages/LoanApplicationsPage/ApplicationStatusInfoMap';
import { LoanApplicationsStore } from 'src/stores/LoanApplicationsStore/LoanApplicationsStore';
import { UserStore } from 'src/stores/UserStore/UserStore';
import styled from 'styled-components';

const store = LoanApplicationsStore;

const START_PROCESS_KEY = 'ManualDealProcess';

export const LoanApplicationsHeaderPanel = observer(() => {
    const { t } = useTranslation();

    return (
        <StyledHeaderWrapper>
            <SearchPanel
                store={store}
                renderPanel={(props) => <FilterPanel {...props} />}
                placeholder={t(
                    'Page.LoanApplication.AsideHeader.Search.Placeholder',
                )}
            />
            {UserStore.can.manage.applications && (
                <Tooltip
                    title={t(
                        'Page.Applications.AsideHeader.CreateNewItem.Tooltip',
                    )}
                >
                    <StyledCreateButton
                        onClick={() => {
                            const targetLink =
                                EntityLinks.applications.process?.(
                                    START_PROCESS_KEY,
                                );
                            targetLink && Router.navigate(targetLink);
                        }}
                    >
                        <PlusOutlined />
                    </StyledCreateButton>
                </Tooltip>
            )}
        </StyledHeaderWrapper>
    );
});

const FilterPanel = observer((props: SearchPanelFilterFormProps) => {
    const statuses = getApplicationStatuses();
    const { t } = useTranslation();

    return (
        <SearchPanelFilterForm
            {...props}
            initialValues={{ issuedAt: [] }}
            onFinish={(values) => {
                const { issuedAt, ...other } = values;
                store.pager.reset();
                const [issueDateFrom, issueDateTo] = issuedAt || [
                    undefined,
                    undefined,
                ];
                issueDateFrom?.set('hour', 0).set('minute', 0).set('second', 0);
                issueDateTo
                    ?.set('hour', 23)
                    .set('minute', 59)
                    .set('second', 59);
                store.filterCriteria?.applyFilter({
                    ...other,
                    issueDateFrom,
                    issueDateTo,
                });
                store.loadList();
            }}
        >
            <StyledSelect
                label={t('Page.LoanApplication.Filter.Status')}
                name="status"
            >
                <Select mode="multiple" allowClear>
                    {statuses.map((item) => (
                        <Select.Option value={item.id} key={item.id}>
                            {t(`data:status.application.${item.id}`)}
                        </Select.Option>
                    ))}
                </Select>
            </StyledSelect>
            <Form.Item
                label={t('Page.LoanApplication.Filter.IssuedAt')}
                name="issuedAt"
            >
                <DateRangePicker />
            </Form.Item>
        </SearchPanelFilterForm>
    );
});

const StyledSelect = styled(Form.Item)`
    .ant-select:not(.ant-select-customize-input) .ant-select-selector {
        display: flex;
        align-items: center;
        min-height: 2.7rem;
        border-radius: 0.5rem;
    }

    .anticon-close-circle {
        svg {
            transform: translate(-14px, -2px);
        }
    }

    .ant-select-clear {
        background: unset;
    }
`;

const StyledHeaderWrapper = styled.header`
    position: relative;
    display: grid;
    grid-template-columns: 1fr auto;
`;

const StyledCreateButton = styled.button`
    width: 3.6rem;
    min-width: 3rem;
    font-weight: 700;
    height: 2.4rem;
    margin-left: 0.8rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    height: 2rem;
    border: none;
    background: var(--btn-bg);
    border-radius: 0.35rem;
    color: #ffffff;
    span: {
        font-size: 1rem;
    }
`;
